import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Calendar, CheckCircle2, Users, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Info d'un bloc de jours futurs (initial ou retard)
export interface JoursFutursBlocInfo {
  // Temps calendaire (créneaux uniques, pas multiplié par le nombre d'ouvriers)
  creneauxCalendaires: number;    // Nombre de demi-journées calendaires uniques
  joursCalendaires: number;       // En jours (creneaux / 2 pour FULL days)
  // Nombre d'ouvriers mobilisés sur cette période
  nbOuvriers: number;
  // Coût total (somme de tous les membres)
  coutTotal: number;
  // Jours-homme total (pour info)
  joursHommeTotal: number;
  // Date de fin planifiée de ce bloc
  dateFinPlanifiee: string;
  // Détail par membre (pour affichage optionnel)
  detailParMembre: Array<{
    membreId: string;
    prenom: string;
    nom: string;
    jours: number;
    cout: number;
  }>;
}

export interface JourFuturInfo {
  // Jours initiaux (affectations normales)
  initial: JoursFutursBlocInfo | null;
  // Jours de retard (affectations avec notes 'Retard chantier%')
  retard: JoursFutursBlocInfo | null;
  // Jours de reprise (affectations avec notes 'Reprise chantier')
  reprise: JoursFutursBlocInfo | null;
  // Totaux combinés pour l'affichage
  totalJours: number;            // Jours-homme total (initial + retard + reprise)
  totalCout: number;             // Coût total
  dateFinPlanifiee: string;      // Date max entre initial, retard et reprise
  nbDemiJournees: number;        // Total demi-journées (pour compatibilité)
  // Temps calendaire combiné (pour affichage ergonomique)
  totalJoursCalendaires: number; // Jours calendaires combinés
  totalNbOuvriers: number;       // Nombre d'ouvriers uniques mobilisés (union des blocs)
  detailParMembre: Array<{
    membreId: string;
    prenom: string;
    nom: string;
    jours: number;
    cout: number;
    nbDemiJournees: number;
  }>;
}

interface TerminerStepJoursFutursProps {
  chantierNom: string;
  joursFuturs: JourFuturInfo;
  onMaintenir: () => void;    // L'utilisateur veut garder les jours
  onRetirer: () => void;      // L'utilisateur veut les supprimer -> passe au screen 2
}

// Formater une date en français
function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return format(date, 'd MMMM yyyy', { locale: fr });
  } catch {
    return dateStr;
  }
}

// Formater le temps calendaire de manière ergonomique (Option 2)
// Affiche le temps calendaire + nombre d'ouvriers si > 1
function formatTempsCalendaire(bloc: JoursFutursBlocInfo): string {
  const { joursCalendaires, creneauxCalendaires, nbOuvriers } = bloc;

  // Formater le temps
  let tempsStr: string;
  if (creneauxCalendaires === 1) {
    tempsStr = 'une demi-journée';
  } else if (creneauxCalendaires === 2 && joursCalendaires === 1) {
    tempsStr = '1 jour';
  } else if (joursCalendaires % 1 === 0) {
    // Jours entiers
    tempsStr = `${joursCalendaires} jour${joursCalendaires > 1 ? 's' : ''}`;
  } else {
    // Jours + demi-journée
    const joursEntiers = Math.floor(joursCalendaires);
    if (joursEntiers === 0) {
      tempsStr = 'une demi-journée';
    } else {
      tempsStr = `${joursEntiers} jour${joursEntiers > 1 ? 's' : ''} et demi`;
    }
  }

  // Ajouter le nombre d'ouvriers si > 1
  if (nbOuvriers > 1) {
    return `${tempsStr} (${nbOuvriers} ouvriers mobilisés)`;
  }

  return tempsStr;
}

// Formater un montant en euros
function formatMontant(montant: number): string {
  return montant.toLocaleString('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + ' €';
}

export function TerminerStepJoursFuturs({
  chantierNom,
  joursFuturs,
  onMaintenir,
  onRetirer,
}: TerminerStepJoursFutursProps) {
  const hasInitial = joursFuturs.initial && joursFuturs.initial.joursCalendaires > 0;
  const hasRetard = joursFuturs.retard && joursFuturs.retard.joursCalendaires > 0;
  const hasReprise = joursFuturs.reprise && joursFuturs.reprise.joursCalendaires > 0;

  return (
    <div className="space-y-6">
      {/* Alerte visuelle */}
      <div className="flex items-start gap-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
        <AlertTriangle className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-lg text-orange-600 dark:text-orange-400">
            Travail planifié non réalisé
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Vous souhaitez clôturer le chantier <strong className="text-foreground">"{chantierNom}"</strong> mais il reste encore du travail planifié jusqu'au{' '}
            <strong className="text-foreground">
              {formatDate(joursFuturs.dateFinPlanifiee)}
            </strong>.
          </p>
        </div>
      </div>

      {/* Bloc Jours Initiaux (si existants) */}
      {hasInitial && joursFuturs.initial && (
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              Travail initial planifié
            </h4>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {formatTempsCalendaire(joursFuturs.initial)} de travail
              </span>
              <span className="font-mono text-blue-600 dark:text-blue-400">
                {formatMontant(joursFuturs.initial.coutTotal)}
              </span>
            </div>

            {/* Détail par membre si plusieurs */}
            {joursFuturs.initial.detailParMembre.length > 1 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                {joursFuturs.initial.detailParMembre.map((m) => (
                  <div key={m.membreId} className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>{m.prenom} {m.nom}</span>
                    <span className="font-mono">{formatMontant(m.cout)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bloc Jours de Retard (si existants) */}
      {hasRetard && joursFuturs.retard && (
        <Card className="border-orange-500/30">
          <CardContent className="p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              Travail de retard planifié
            </h4>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {formatTempsCalendaire(joursFuturs.retard)} de travail
              </span>
              <span className="font-mono text-orange-600 dark:text-orange-400">
                {formatMontant(joursFuturs.retard.coutTotal)}
              </span>
            </div>

            {/* Détail par membre si plusieurs */}
            {joursFuturs.retard.detailParMembre.length > 1 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                {joursFuturs.retard.detailParMembre.map((m) => (
                  <div key={m.membreId} className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>{m.prenom} {m.nom}</span>
                    <span className="font-mono">{formatMontant(m.cout)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bloc Jours de Reprise (si existants) */}
      {hasReprise && joursFuturs.reprise && (
        <Card className="border-blue-500/30">
          <CardContent className="p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              Travail de reprise planifié
            </h4>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {formatTempsCalendaire(joursFuturs.reprise)} de travail
              </span>
              <span className="font-mono text-blue-600 dark:text-blue-400">
                {formatMontant(joursFuturs.reprise.coutTotal)}
              </span>
            </div>

            {/* Détail par membre si plusieurs */}
            {joursFuturs.reprise.detailParMembre.length > 1 && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-1">
                {joursFuturs.reprise.detailParMembre.map((m) => (
                  <div key={m.membreId} className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>{m.prenom} {m.nom}</span>
                    <span className="font-mono">{formatMontant(m.cout)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Total combiné si au moins deux types existent */}
      {[hasInitial, hasRetard, hasReprise].filter(Boolean).length >= 2 && (
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex justify-between items-center font-semibold">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                Coût total à supprimer
              </span>
              <span className="font-mono text-orange-600 dark:text-orange-400">
                {formatMontant(joursFuturs.totalCout)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question principale */}
      <div className="space-y-4">
        <p className="font-medium text-center">
          Souhaitez-vous maintenir ces jours dans les coûts ?
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Option OUI - Maintenir */}
          <Card
            className={cn(
              'cursor-pointer transition-all hover:border-green-500/50 hover:bg-green-500/5',
              'border-2'
            )}
            onClick={onMaintenir}
          >
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="font-semibold">Oui, les maintenir</p>
              <p className="text-xs text-muted-foreground mt-1">
                Les coûts restent comptabilisés
              </p>
            </CardContent>
          </Card>

          {/* Option NON - Retirer */}
          <Card
            className={cn(
              'cursor-pointer transition-all hover:border-orange-500/50 hover:bg-orange-500/5',
              'border-2'
            )}
            onClick={onRetirer}
          >
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <p className="font-semibold">Non, les retirer</p>
              <p className="text-xs text-muted-foreground mt-1">
                Supprimer ces jours du planning et des coûts
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
